import { useCallback, useEffect, useReducer, useRef } from "react";
import { useStudio } from "@/store/useStudio";

// Design-relevant slice captured by the undo/redo history. Zustand replaces the reference of
// any slice it mutates, so reference-equality is enough to detect a discrete change.
const SLICE_KEYS = [
  "selections", "areas", "manualAreas", "systemBlocks", "targets",
  "allocationMode", "scenarioFaces", "locationAreas", "locationBlocks", "resourceBlocks",
];

function snap(s) {
  const o = {};
  SLICE_KEYS.forEach((k) => { o[k] = s[k]; });
  return o;
}
function unchanged(a, b) {
  return SLICE_KEYS.every((k) => a[k] === b[k]);
}

// Model-bound Undo/Redo. Captures discrete design actions (add system, change area, draft, …),
// caps history at `max` states, and fully resets whenever the active 3D model changes/clears.
export function useUndo(max = 10) {
  const past = useRef([]);
  const future = useRef([]);
  const applying = useRef(false);
  const modelKey = useRef(null);
  const [, force] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    const unsub = useStudio.subscribe((state, prev) => {
      const mk = state.model?.status === "ready" ? (state.model.name || "ready") : null;
      if (mk !== modelKey.current) {
        modelKey.current = mk;
        past.current = [];
        future.current = [];
        applying.current = false;
        force();
        return;
      }
      if (applying.current || !modelKey.current) return;
      if (unchanged(snap(state), snap(prev))) return;
      past.current.push(snap(prev));
      if (past.current.length > max) past.current.shift();
      future.current = [];
      force();
    });
    return unsub;
  }, [max]);

  const undo = useCallback(() => {
    if (!past.current.length) return;
    future.current.push(snap(useStudio.getState()));
    if (future.current.length > max) future.current.shift();
    const prev = past.current.pop();
    applying.current = true;
    useStudio.setState(prev);
    Promise.resolve().then(() => { applying.current = false; });
    force();
  }, [max]);

  const redo = useCallback(() => {
    if (!future.current.length) return;
    past.current.push(snap(useStudio.getState()));
    if (past.current.length > max) past.current.shift();
    const next = future.current.pop();
    applying.current = true;
    useStudio.setState(next);
    Promise.resolve().then(() => { applying.current = false; });
    force();
  }, [max]);

  return { undo, redo, canUndo: past.current.length > 0, canRedo: future.current.length > 0 };
}
